import React, { useState } from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import { capitalize } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import { localeDetails } from '~/components/CmsMain/localeGlobals';
import { getAllSearchableContactRoles } from '~/components/communications/ContactUtils';
import ContactTextFieldFormik from '~/components/ContactTextFieldFormik';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import Caption from '~/components/core/TextComponents/Caption';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import useOrganization from '~/components/OrganizationContext';
import TextFieldFormik, { MonetaryValueTextFieldFormik } from '~/components/TextFieldFormik';
import { reportAxiosError } from '~/Utils';
import { extractError } from '~/Utils/errors/extractError';
import { isPermissionDeniedError } from '~/Utils/errors/permissionDenined';

import { claimRecoveryRequestsUrl } from './useRecoveryRequests';

// The statuses the create route documents as carrying a message the adjuster can act on: an
// invalid address or an opted-out payer (502), a provider that did not answer (504), the gate
// refusals (403), and a bad reference (400). Shown in the dialog itself rather than through the
// generic error popup, so the payer or the amount can be corrected against what the provider
// said - relaying the provider's own words is the reason the send is synchronous at all. Anything
// else falls through to reportAxiosError, as the sibling recovery dialogs do.
const RELAYED_ERROR_STATUSES = [400, 403, 502, 504];

interface SendRecoveryRequestDialogProps {
  exposure: { id: number; isGeneralExpenses?: boolean };
  onClose: () => void;
  onUpdate: () => void | Promise<void>;
}

interface SendRecoveryRequestFormValues {
  payer_contact_id: number | '';
  payable_type: string;
  general_recovery_type: string;
  requested_amount: number | '';
}

const SendRecoveryRequestDialog: React.FC<SendRecoveryRequestDialogProps> = ({ exposure, onClose, onUpdate }) => {
  const { classes } = useStyles();
  const { claim } = useClaim({ claimRequired: true });
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationContactRolesDict } = useOrganization();
  const { generalRecoveriesConfiguration } = usePaymentsConfiguration();
  const [failureMessage, setFailureMessage] = useState<string | null>(null);

  // mirrors exposureIdOrNullIfGeneralExpenses: general expenses is the claim-level bucket, which
  // the request carries as no exposure at all
  const exposureId = exposure.isGeneralExpenses ? null : exposure.id;
  const recoveryTypeConfiguration = generalRecoveriesConfiguration?.general_recovery_type;
  const recoveryTypeOptions: { value: string; label: string }[] = recoveryTypeConfiguration?.active
    ? (recoveryTypeConfiguration.options ?? [])
        .filter((option: { id?: string; active?: boolean }) => option.active && option.id)
        .map((option: { id: string; desc?: string }) => ({ value: option.id, label: option.desc || option.id }))
    : [];

  const handleSubmit = async (
    values: SendRecoveryRequestFormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setFailureMessage(null);
    try {
      await axios.post(claimRecoveryRequestsUrl(claim.organization_id, claim.id), {
        exposure_id: exposureId,
        payable_type: values.payable_type,
        ...(values.general_recovery_type ? { general_recovery_type: values.general_recovery_type } : {}),
        requested_amount: Number(values.requested_amount),
        // the claim's own currency, falling back to the organization's, as the payment-request
        // dialog does. localeDetails is exported from a .js module typed as `null` until populated
        // at app startup.
        currency: claim?.policy?.policy_currency || (localeDetails as { currency?: string } | null)?.currency,
        payer_contact_id: values.payer_contact_id,
      });
      await onUpdate();
      onClose();
    } catch (error: unknown) {
      // A permission-denied 403 carries a permission_id rather than a message and has a dialog of
      // its own; it is not one of the refusals this branch relays.
      if (
        axios.isAxiosError(error) &&
        RELAYED_ERROR_STATUSES.includes(error.response?.status ?? 0) &&
        !isPermissionDeniedError(error)
      ) {
        setFailureMessage(extractError(error).message);
        setSubmitting(false);

        return;
      }
      await reportAxiosError(error);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={
        {
          payer_contact_id: '',
          // A claim-level indemnity request is refused by the create route, because no
          // general-recovery write path could ever post its settlement - so general expenses opens
          // on Expenses and is locked there, exactly as the Add Recovery Amount dialog locks it.
          payable_type: exposureId === null ? 'expenses' : '',
          general_recovery_type: '',
          requested_amount: '',
        } as SendRecoveryRequestFormValues
      }
      validationSchema={Yup.object().shape({
        payer_contact_id: Yup.number().required('Required'),
        payable_type: Yup.string().required('Required'),
        general_recovery_type: Yup.string(),
        requested_amount: Yup.number().required('Required').moreThan(0, 'Must be greater than 0'),
      })}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, handleSubmit }) => (
        <CardDialog
          open
          isDialog
          fullWidth
          maxWidth="sm"
          onClose={onClose}
          title="Send Recovery Request"
          preventClose={isSubmitting}
        >
          <div className="grid gap-12">
            {failureMessage === null ? null : (
              <AlertBanner alertType={AlertBanner.ALERT_TYPES.ERROR} note={failureMessage} withIcon />
            )}
            {/* The two controls the Add Recovery Amount dialog captures the same two facts with.
                The payer especially: the same role-filtered contact search, over the same accepted
                roles, so the payers this dialog can name are the payers the recovery that settles
                the request can be recorded against - the settlement passes the request's payer
                straight through as the recovery's. Its id is `payer_contact`, which is what writes
                the `payer_contact_id` the create route takes. */}
            <Caption variant={Caption.VARIANTS.LABEL}>Payable Type</Caption>
            <RadioButtonGroupFormik
              id="payable_type"
              options={['indemnity', 'expenses'].map((payableType) => ({
                optionValue: payableType,
                text: capitalize(payableType),
              }))}
              showOnly={isSubmitting || exposureId === null}
            />
            <div className="w-1/2">
              <ContactTextFieldFormik
                id="payer_contact"
                label="Payer"
                acceptedRoles={getAllSearchableContactRoles(organizationContactRolesDict)}
                fixedSearchResults
                fullWidth
                disabled={isSubmitting}
              />
            </div>
            {recoveryTypeOptions.length === 0 ? null : (
              <TextFieldFormik
                id="general_recovery_type"
                label="Recovery Type"
                select
                fullWidth
                disabled={isSubmitting}
                // optional: a lone configured type must not fill itself in as if it were picked
                autoSelectSingleOption={false}
              >
                {/* @ts-expect-error - TextFieldFormik not yet typed and its type inferrence is flawed */}
                {recoveryTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextFieldFormik>
            )}
            <div className="w-1/2">
              <MonetaryValueTextFieldFormik
                id="requested_amount"
                label="Amount"
                className={classes.textField}
                fullWidth
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className={classes.buttonsContainer}>
            <CancelButton content="Cancel" disabled={isSubmitting} onClick={onClose} />
            <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
              Send
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default SendRecoveryRequestDialog;
